import { Tooltip, Badge, Box } from '@mui/material';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import CalendarTodayRoundedIcon from '@mui/icons-material/CalendarTodayRounded';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { useSelector } from 'react-redux';
import { selectTimeRangeDT } from '@/features/energyxplorer/redux/timeRangeSlice';
import {
  convertTimestampsSummaryToDateTimes,
  getAdjustedTimeRange,
  isCompletelyOutside,
  isOutOfBounds,
} from '@/features/energyxplorer/utils/timestampsSummaryHelpers';

export default function ConsumptionWarningIndicator({ data }) {
  const { t } = useTranslation('energyxplorer-energy-overview-tab');
  const timeRange = useSelector(selectTimeRangeDT);
  if (!data) return null;

  // Main meter summary
  const mainTimestampsSummary = {
    maxEarliestDataTimestamp: DateTime.fromISO(
      data.EnergyDataSummary.StartTimestamp,
    ),
    minLatestDataTimestamp: DateTime.fromISO(
      data.EnergyDataSummary.EndTimestamp,
    ),
  };

  const completeTimestampsSummary = convertTimestampsSummaryToDateTimes(
    data.TimestampsSummary,
  );

  // SEU summaries (all sub distribution boards)
  const seuSummaries = data.EnergyDistributionSummary.filter((summary) => {
    const seuTypesToFilter = ['IkkeAnalyseret', 'Analyseret'];
    return !seuTypesToFilter.includes(summary.Name);
  }).map((summary) => {
    return convertTimestampsSummaryToDateTimes(summary.TimestampsSummary);
  });

  // Check for missing data
  const mainMissing = isOutOfBounds(mainTimestampsSummary, timeRange);
  const seuMissing = seuSummaries.some((summary) =>
    isOutOfBounds(summary, timeRange),
  );
  const noOverlapBetweenMainMeterAndNodes = seuSummaries.every((summary) =>
    isCompletelyOutside(summary, timeRange),
  );

  let warning = null;

  const adjustedTimeRange = getAdjustedTimeRange(
    completeTimestampsSummary,
    timeRange,
  );

  const adjustedTimeRangeString = `${adjustedTimeRange.start.toLocaleString(DateTime.DATE_SHORT)} - ${adjustedTimeRange.end.toLocaleString(DateTime.DATE_SHORT)}`;

  if (mainMissing && seuMissing) {
    warning = t('consumption_card.warnings.missing_node_and_main_meter_data', {
      timerange: adjustedTimeRangeString,
    });
  } else if (mainMissing) {
    warning = t('consumption_card.warnings.missing_main_meter_data', {
      timerange: adjustedTimeRangeString,
    });
  } else if (seuMissing) {
    warning = t('consumption_card.warnings.missing_node_data', {
      timerange: adjustedTimeRangeString,
    });
  } else if (noOverlapBetweenMainMeterAndNodes) {
    warning = t('consumption_card.warnings.missing_overlap');
  }

  if (!warning) return null;

  return (
    <Tooltip title={<Box whiteSpace={'pre-line'}>{warning}</Box>}>
      <Badge
        badgeContent={<WarningRoundedIcon fontSize="small" color="warning" />}
        sx={{ mr: 1 }}
      >
        <CalendarTodayRoundedIcon fontSize="small" />
      </Badge>
    </Tooltip>
  );
}
